import { api, pushAPI } from '../services/api';

/**
 * Browser push (Sep-20 parity, P6-02). Subscribes this browser with the
 * platform's VAPID key and registers the endpoint with the server. Every
 * step that can be unsupported (no service worker, no Notification API,
 * permission denied, no key on the server) returns a plain reason instead
 * of throwing, so the settings switch can say why it is off.
 */
const urlBase64ToUint8Array = (base64) => {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const raw = window.atob((base64 + padding).replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
};

// The worker has no page to ask, so it is told the API's address in its own
// URL: it needs it to Approve / Reject straight from a notification.
const workerUrl = () => `/push-sw.js?api=${encodeURIComponent(new URL(api.defaults.baseURL || '/', window.location.origin).origin)}`;

/** A worker registered before it took ?api= is swapped for one that does (same subscription). */
export const refreshWorker = async () => {
  if (!pushSupported()) return;
  const reg = await navigator.serviceWorker.getRegistration('/push-sw.js');
  const current = reg?.active?.scriptURL || '';
  if (reg && !current.endsWith(workerUrl())) await navigator.serviceWorker.register(workerUrl());
};

/**
 * The registration once its worker is active. `register()` resolves as soon
 * as the worker is registered, while it is still installing - and a browser
 * refuses a push subscription until a worker is active ("Subscription failed
 * - no active Service Worker"), which is every first visit.
 */
export const activeRegistration = async () => {
  const reg = await navigator.serviceWorker.register(workerUrl());
  if (reg.active) return reg;
  const worker = reg.installing || reg.waiting;
  if (worker && worker.state !== 'activated') {
    await new Promise((resolve, reject) => {
      const onChange = () => {
        if (worker.state === 'activated') { worker.removeEventListener('statechange', onChange); resolve(); }
        if (worker.state === 'redundant') { worker.removeEventListener('statechange', onChange); reject(new Error('The notification worker could not start. Reload the page and try again.')); }
      };
      worker.addEventListener('statechange', onChange);
    });
  }
  return reg;
};

export const pushSupported = () => typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

export const currentSubscription = async () => {
  if (!pushSupported()) return null;
  const reg = await navigator.serviceWorker.getRegistration('/push-sw.js');
  return reg ? reg.pushManager.getSubscription() : null;
};

export const enablePush = async () => {
  if (!pushSupported()) return { ok: false, reason: 'This browser does not support push notifications.' };
  const { data: cfg } = await pushAPI.config();
  if (!cfg.web || !cfg.vapid_public_key) return { ok: false, reason: 'Push is not configured on the server yet.' };
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return { ok: false, reason: 'Notifications were not allowed in the browser.' };
  const reg = await activeRegistration();
  const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(cfg.vapid_public_key) });
  const json = sub.toJSON();
  await pushAPI.subscribe({ kind: 'web', endpoint: json.endpoint, keys: json.keys });
  return { ok: true };
};

export const disablePush = async () => {
  const sub = await currentSubscription();
  if (sub) {
    try { await pushAPI.unsubscribe(sub.endpoint); } catch { /* the server row may already be gone */ }
    await sub.unsubscribe();
  }
  return { ok: true };
};
